// Hudl XHR Library
// ------------------
// thin wrapper around jQuery's ajax alias methods that
// retries requests that fail.

"use strict";

var $ = require('jquery');
var _h = require('./utils');

var slice = Array.prototype.slice;
var noop = function() { };
var defaultAjaxOptions = {
  contentType: 'application/json; charset=utf-8',
  dataType: 'json',
  converters: {
    'text json': function jsonConverter(result) {
      // be more lenient with results (allow for void-returning actions)
      return result ? JSON.parse(result) : null;
    },
  },
  hudlResponseWrapper: function(promise) {
    return promise;
  },
  retry: true,
  maxRetries: 3,
  initialRetryDelay: 150,
  retryDelay: function(lastDelay, attempt) {
    var idx = _h.clamp(attempt - 1, 0, 2); // the attempt will be 1,2,or 3.
    return [2000, 6000, 12000][idx];
  },
};

function bindLegacyCallsToPromise(promise, complete, success, error) {
  complete = complete || noop;
  success = success || noop;
  error = error || noop;
  return promise.always(complete).done(success).fail(error);
}

var httpMethods = ['post', 'put', 'patch', 'delete', 'get'];

// certain HTTP status codes should not be retried
var passThroughHttpStatusCodes = [401, 403, 405, 410];

// constructor
var HudlAjax = function HudlAjaxConstructor(options) {
  options = options || {}; // ensure we have a proper default
  // another ajax lib can be injected via options
  var config = this.config = $.extend({
    ajax: $.ajax,
  }, options);

  if (options.disableRetry) {
    this.config.ajaxOptions = this.config.ajaxOptions || {};
    this.config.ajaxOptions.retry = false;
    this.config.ajaxOptions.maxRetries = 0;
    this.config.ajaxOptions.retryDelay = 0;
  }

  // alias all the supported HTTP methods as instance members. Doing this in
  // the ctor binds each method to the current instance, rather than binding
  // them to the prototype or having to do some sort of cache with an apply() call.
  var instance = this;
  httpMethods.forEach(function(method) {
    // this creates a new method where the first argument is already specified.
    instance[method] = instance.makeRequest.bind(instance, method.toUpperCase());
  });

  // this is a jank way for us to expose the defaultAjaxOptions to
  // our unit tests
  if (options.settingsCallback) {
    options.settingsCallback(defaultAjaxOptions);
  }
};

var requestIdHeader = 'X-Hudl-RequestId';

/**
* makeRequest() returns a new AJAX request
* based on the passed in arguments
*
* @param {String} method
* @param {String} url
* @param {Object} data
* @param {Object} ajaxOptions
* @param {Function} callback
*
* @return {Promise} promise
*/
HudlAjax.prototype.makeRequest = function(method, url, data, ajaxOptions, callback) {
  ajaxOptions = ajaxOptions || {};
  var dataType;
  if (typeof (ajaxOptions) === 'string') {
    dataType = ajaxOptions;
    ajaxOptions = {};
  }

  var options = $.extend({}, defaultAjaxOptions, this.config.ajaxOptions || {}, {
    type: method,
    url: url,
    // don't stringify GET requests or null data
    data: method === 'GET' || !data ? data : JSON.stringify(data),
  }, ajaxOptions);

  var def = $.Deferred();
  var promise = def.promise();
  var retries = options.retry ? options.maxRetries : 0;
  var retryAttempt = 0;
  var ajax = this.config.ajax;
  var retryCallback = this.config.onRetry || noop;
  var retryDelay = options.initialRetryDelay;

  // request specific modifications
  if (method === 'GET') {
    // we need to support our current codebase which used to call jQuery.get() directly.
    // jQuery doesn't set a dataType/contentType on GET requests by default, so we remove them.
    // Otherwise this will throw an error because jquery will try to convert any text data returned to JSON
    options.dataType = undefined;
    options.contentType = undefined;

    // there's always a catch though. If someone has specified a dataType
    // either by passing it in as part of ajaxOptions, OR by passing a string
    // instead of ajaxOptions, we should use it
    if (dataType || ajaxOptions.dataType) {
      options.dataType = dataType || ajaxOptions.dataType;
    }
  }

  if (ajaxOptions.params) {
    options.url += /\?/.test(options.url) ? '&' : '?';
    options.url += $.param(ajaxOptions.params);
    ajaxOptions.params = undefined;
  }

  if (callback) {
    // mimic jquery behaviour as much as possible
    // overwrite success with the passed callback (if it's there)
    ajaxOptions.success = callback; // yeah, don't set both of these..
  }

  if (ajaxOptions.complete || ajaxOptions.success || ajaxOptions.error) {
    // if caller used legacy complete, success, error bindings
    // copy them to promise handlers.
    bindLegacyCallsToPromise(promise, ajaxOptions.complete, ajaxOptions.success, ajaxOptions.error);

    // remove the handlers from the options object so we
    // don't accidentally re-bind them to retrys
    options.complete = options.success = options.error = null;
  }

  promise.complete = $.Callbacks('once memory').add;
  promise.error = promise.fail;
  promise.success = promise.done;

  var requestId; // scope this variable to the send() block below
  function send(deferred) {
    retries--;
    retryAttempt = options.maxRetries - retries;

    // compute our retry delay, based on the number of retries
    if (typeof options.retryDelay === 'function') {
      retryDelay = options.retryDelay(retryDelay, retryAttempt);
    } else {
      retryDelay = options.retryDelay;
    }

    // make the HTTP request, store the promise returned by jQuery
    var innerPromise = options.hudlResponseWrapper(ajax(options));

    // bind our own handlers to the inner promise's outcome
    // and reject/resolve our outter promise (the one we gave to the caller)
    // based on whether we can retry this request or not
    return innerPromise.done(function() {
      // when the call goes through, just resolve the outter promise
      deferred.resolveWith(this, slice.call(arguments));
    }).fail(function(jqXHR /*...rest*/) {
      // if we get a http status that we should not retry
      // reject the outer promise immediately
      if (jqXHR && passThroughHttpStatusCodes.indexOf(jqXHR.status) !== -1) {
        deferred.rejectWith(this, slice.call(arguments));
        return;
      }

      // if we recieved a requestId from the server, store it and re-send it on retried requests
      if (!requestId && jqXHR && jqXHR.getResponseHeader && jqXHR.getResponseHeader(requestIdHeader)) {
        requestId = jqXHR.getResponseHeader(requestIdHeader);
        options.headers = $.extend({}, options.headers, {
          'X-Hudl-AJAX-Original-Request-ID': requestId,
        });
      }

      // if we're retrying the request, send which retry this is
      if (options.maxRetries - retries >= 0) {
        options.headers = $.extend({}, options.headers, {
          'X-Hudl-AJAX-Retry-Request-Count': (options.maxRetries - retries),
        });
      }
      // when an error occurs, check the number of retries left
      // if we can retry the re-issue the call
      if (retries >= 0) {
        var opts = retryCallback(options);
        if (opts) {
          $.extend(options, opts);
        }
        // if a retry delay is specified use it,
        // retryDelay = 0 will cause the retries to happen immediately
        if (retryDelay) {
          setTimeout(function() {
            send(deferred);
          }, retryDelay);
        } else {
          send(deferred);
        }
      } else {
        deferred.rejectWith(this, slice.call(arguments));
      }
    });
  }

  // make the initial request
  send(def);

  // return the outter promise to the caller
  return promise;
};

HudlAjax.instance = new HudlAjax();
module.exports = HudlAjax;
